import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";

const missingProviderError = new Error(
  "Ongoing treatment rollout status is unavailable outside its provider.",
);

const missingProviderValue = Object.freeze({
  enabled: false,
  openCaseCount: null,
  loading: false,
  error: missingProviderError,
  refresh: async () => null,
});

const OngoingTreatmentRolloutContext = createContext(null);

function parseRollout(data) {
  if (
    !data
    || Array.isArray(data)
    || typeof data !== "object"
    || typeof data.enabled !== "boolean"
    || typeof data.open_case_count !== "number"
    || !Number.isInteger(data.open_case_count)
    || data.open_case_count < 0
    || data.managed_during_pilot !== true
  ) {
    throw new Error("Ongoing treatment rollout returned an invalid response.");
  }

  return {
    enabled: data.enabled,
    openCaseCount: data.open_case_count,
  };
}

export async function resolveOngoingTreatmentEnabled({ enabled, refresh }) {
  if (enabled === true) return true;
  const refreshed = await refresh?.();
  return refreshed?.enabled === true;
}

export function OngoingTreatmentRolloutProvider({ children }) {
  const { user, activeClinicId } = useAuth();
  const userId = user?.id || null;
  const identityKey = userId && activeClinicId ? `${userId}:${activeClinicId}` : null;
  const requestSequence = useRef(0);
  const mounted = useRef(true);
  const [state, setState] = useState({
    identityKey: null,
    enabled: false,
    openCaseCount: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestSequence.current += 1;
    };
  }, []);

  const refresh = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!identityKey) {
      setState({
        identityKey: null,
        enabled: false,
        openCaseCount: null,
        loading: false,
        error: null,
      });
      return null;
    }

    setState({
      identityKey,
      enabled: false,
      openCaseCount: null,
      loading: true,
      error: null,
    });

    try {
      if (typeof supabase?.rpc !== "function") {
        throw new Error("Ongoing treatment rollout provider is unavailable.");
      }
      const result = await supabase.rpc("get_ongoing_treatment_rollout");
      if (result?.error) throw result.error;
      const parsed = parseRollout(result?.data);
      if (!mounted.current || requestId !== requestSequence.current) return null;
      setState({ identityKey, ...parsed, loading: false, error: null });
      return parsed;
    } catch (readError) {
      if (!mounted.current || requestId !== requestSequence.current) return null;
      setState({
        identityKey,
        enabled: false,
        openCaseCount: null,
        loading: false,
        error: readError instanceof Error
          ? readError
          : new Error(readError?.message || "Could not read ongoing treatment rollout status."),
      });
      return null;
    }
  }, [identityKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onImpersonationChanged = () => { refresh(); };
    window.addEventListener("dental-os:impersonation-session-started", onImpersonationChanged);
    window.addEventListener("dental-os:impersonation-session-ended", onImpersonationChanged);
    return () => {
      window.removeEventListener("dental-os:impersonation-session-started", onImpersonationChanged);
      window.removeEventListener("dental-os:impersonation-session-ended", onImpersonationChanged);
    };
  }, [refresh]);

  useEffect(() => {
    // The pilot flag is managed server-side and can change while a clinic
    // keeps Dental OS open all day. Re-check when the browser comes back so
    // entry points do not remain hidden behind a stale, session-old value.
    const onFocus = () => { refresh(); };
    const onOnline = () => { refresh(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [refresh]);

  const value = useMemo(() => {
    if (!identityKey) {
      return { enabled: false, openCaseCount: null, loading: false, error: null, refresh };
    }
    if (state.identityKey !== identityKey) {
      return { enabled: false, openCaseCount: null, loading: true, error: null, refresh };
    }
    return {
      enabled: state.enabled,
      openCaseCount: state.openCaseCount,
      loading: state.loading,
      error: state.error,
      refresh,
    };
  }, [identityKey, refresh, state]);

  return (
    <OngoingTreatmentRolloutContext.Provider value={value}>
      {children}
    </OngoingTreatmentRolloutContext.Provider>
  );
}

export function useOngoingTreatmentRollout() {
  return useContext(OngoingTreatmentRolloutContext) || missingProviderValue;
}
